import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useMemo } from 'react';
import { useLocalizeMessage, Table, Select } from 'libs.nucleus';
import { useWorkspaceSiteColumns } from './workspace_sites_columns.hook';
import { useWorkspaceSites, SortColumn } from '../../hooks';
export const WorkspaceSitesTable = ({ selectedIds = [], onRowSelectionChange, actionBarProps, }) => {
    const translate = useLocalizeMessage();
    const { workspaceSiteColumns } = useWorkspaceSiteColumns();
    const initialPagination = { pageIndex: 0, pageSize: 10 };
    const { isLoading, error, workspaceSites, pagination, totalPages, sort, noPiiFilter, onPaginationChange, onSearchChange, onSortChange, onClearSort, onFilterChange, } = useWorkspaceSites(initialPagination);
    const sortingState = sort.column ? [{ id: sort.column, desc: sort.direction === 'desc' }] : [];
    const handleSortingChange = (updater) => {
        const newSorting = typeof updater === 'function' ? updater(sortingState) : updater;
        if (Array.isArray(newSorting) && newSorting.length > 0) {
            const { id, desc } = newSorting[0];
            if (id === SortColumn.USER_COUNT || id === SortColumn.STUDY_COUNT) {
                const direction = desc ? 'desc' : 'asc';
                onSortChange(id, direction);
            }
        }
        else {
            // Clear sort when array is empty
            onClearSort();
        }
    };
    const handleSearch = (value) => {
        onSearchChange(value || '');
    };
    const handleSearchClear = () => {
        onSearchChange('');
    };
    const registrationFilterOptions = useMemo(() => [
        { label: translate('All'), value: 'all' },
        { label: translate('Email'), value: 'email' },
        { label: translate('Username'), value: 'username' },
    ], [translate]);
    const handleFilterChange = (option) => {
        const { value } = option;
        if (value === 'all') {
            onFilterChange(null);
        }
        else if (value === 'email') {
            onFilterChange(false);
        }
        else if (value === 'username') {
            onFilterChange(true);
        }
    };
    const getFilterValue = () => {
        const valueMap = {
            null: 'all',
            true: 'username',
            false: 'email',
        };
        const targetValue = valueMap[String(noPiiFilter)];
        return registrationFilterOptions.find((opt) => opt.value === targetValue) || registrationFilterOptions[0];
    };
    const canDeleteSite = useCallback((site) => {
        return (site.userCount ?? 0) === 0 && (site.studyCount ?? 0) === 0;
    }, []);
    const getDisableReason = useCallback((site) => {
        const hasUsers = (site.userCount ?? 0) > 0;
        const hasStudies = (site.studyCount ?? 0) > 0;
        if (hasUsers && hasStudies) {
            return translate('Cannot delete sites with users and associated environment sites');
        }
        if (hasUsers) {
            return translate('Cannot delete sites with users');
        }
        if (hasStudies) {
            return translate('Cannot delete sites that have associated environment sites');
        }
        return '';
    }, [translate]);
    const handleRowSelectionChange = useCallback((newSelectedIds) => {
        onRowSelectionChange?.(newSelectedIds, workspaceSites || []);
    }, [onRowSelectionChange, workspaceSites]);
    if (error) {
        return (_jsx("div", { className: 'nucleus-text-center nucleus-p-4', children: _jsx("p", { className: 'nucleus-text-error-600', children: translate('Failed to load workspace sites') }) }));
    }
    return (_jsx(Table, { "data-testid": 'workspace-sites-table', isLoading: isLoading, isSelectable: true, selectedIds: selectedIds, onRowSelectionChange: handleRowSelectionChange, getRowId: (row) => row.id.toString(), enableRowSelection: (row) => canDeleteSite(row.original), disableRowSelectionReason: (originalRow) => getDisableReason(originalRow), actionBarProps: actionBarProps, enableSorting: true, hasPagination: true, manualPagination: true, manualPaginationState: pagination, manualSorting: true, manualSortingState: sortingState, initialPageSize: 10, pageCount: totalPages, paginationNavigationScreenReaderLabel: translate('Sites table navigation'), labelNoResults: translate('No Sites'), useInternalSearch: true, labelSearchField: translate('Search by name or ID'), columns: workspaceSiteColumns, data: workspaceSites || [], onPaginationChange: onPaginationChange, onSortingChange: handleSortingChange, onSearch: handleSearch, onSearchClear: handleSearchClear, customHeaderContent: _jsx(Select, { label: translate('Registration override'), options: registrationFilterOptions, value: getFilterValue(), onChange: handleFilterChange }) }));
};
